import { OPEN_STATES } from "./plannerQuery"

// The calendar read's proposals and events as the page shows them: one kind of item, filtered and queued in the page.

export const itemOfProposal = proposal => ({ ...proposal, kind: "proposal", key: "proposal|" + proposal.id, label: proposal.number, proposal })
// A published event: its number, and the proposal's too when it was published from one (story 9). An event without a club of the
// platform is shown without one.
export const itemOfEvent = event => ({
  ...event, kind: "event", key: "event|" + event.id, state: "published", club: event.club ? { kind: "club", ...event.club } : { kind: "none" },
  label: event.proposal ? `${event.proposal.number} · Event #${event.eventId}` : `Event #${event.eventId}`, warnings: [], event,
})
// A confirmed proposal with what Ready to publish says of it (Phase E2p, joined by id): complete, or what publishing still needs.
export const withPublishing = (items, toPublish) => {
  if (!toPublish) return items
  const rows = new Map(toPublish.proposals.map(row => [row.id, { complete: row.complete, problems: row.problems, competitions: row.competitions }]))
  return items.map(item => (item.kind === "proposal" && item.state === "confirmed" && rows.has(item.id) ? { ...item, toPublish: rows.get(item.id) } : item))
}
export const byStart = (a, b) => a.days.start.localeCompare(b.days.start) || a.label.localeCompare(b.label)
export const itemsOf = calendar => [...(calendar?.proposals || []).map(itemOfProposal), ...(calendar?.events || []).map(itemOfEvent)].sort(byStart)

// The key a filter reads for an item: no organizer, and no county („Fără județ”), are choices of their own.
export const NONE = "none"
export const organizerKey = item => item.organizer?.id || NONE
export const countyKey = item => item.countyCode || NONE
export const regionKey = item => item.region || NONE

const DAY_MS = 24 * 3600 * 1000
// A wait for the organizer's answer longer than 14 days ("Waiting > 14 days", ticket 15 Q6; Waiting for answer, ticket 16).
export const waitsLong = (waitingSince, now) => Boolean(waitingSince) && now - Date.parse(waitingSince) > 14 * DAY_MS
// A warning still open: not acknowledged, or acknowledged on facts that changed since.
export const openWarnings = item => (item.warnings || []).filter(warning => warning.open)
// The queues (ticket 15, B3 pinned decisions): each picks the items it holds; "Waiting > 14 days" counts the wait in whole days.
// "Confirmed, incomplete" and "Ready to publish" read the publication's rule (Phase E2p, `withPublishing`), so they show once it is read.
export const QUEUES = [
  { key: "drafts", label: "Drafts", holds: item => item.state === "draft" },
  { key: "waiting", label: "Waiting for answer", holds: item => item.state === "proposed" },
  { key: "waitingLong", label: "Waiting > 14 days",
    holds: (item, now) => item.state === "proposed" && waitsLong(item.waitingSince, now) },
  { key: "incomplete", label: "Confirmed, incomplete", publishing: true, holds: item => item.toPublish?.complete === false },
  { key: "readyToPublish", label: "Ready to publish", publishing: true, holds: item => item.toPublish?.complete === true },
  { key: "warnings", label: "With warnings", holds: item => item.kind === "proposal" && openWarnings(item).length > 0 },
  // A generated name to check (ticket 17), until it is changed or confirmed.
  { key: "checkName", label: "Check the name", holds: item => item.kind === "proposal" && Boolean(item.checkName) },
]

// The filters the page applies to the read, each "any" when nothing is chosen: the key of the query and what it reads of an item.
export const FACETS = { categories: item => item.category, organizers: organizerKey, counties: countyKey, regions: regionKey }
const keeps = (item, query, skipped) => Object.entries(FACETS).every(([facet, keyOf]) => facet === skipped || !query[facet].length
  || query[facet].includes(keyOf(item)))
// What the List can show: published events when they are shown, never an ended tournament (story 7); with "Only with warnings", the
// proposals with an open warning (ticket 13 Q11). The Season and Month views pass no `today`: they show the ended tournaments too.
const listable = (item, query, today) => item.days.end >= today && (query.published || item.kind !== "event")
  && (!query.warnings || (item.kind === "proposal" && openWarnings(item).length > 0))
export const filterItems = (items, query, today) => items.filter(item => listable(item, query, today) && keeps(item, query))
// A filter's choices with their counts: the items each would keep with the other filters as they are.
export const facetCounts = (items, query, today, facet) => {
  const counts = new Map()
  items.filter(item => listable(item, query, today) && keeps(item, query, facet)).forEach(item => {
    const key = FACETS[facet](item)
    counts.set(key, { item, count: (counts.get(key)?.count || 0) + 1 })
  })
  return counts
}
// A published event's registrations are open (ticket 14): their first stage has started (the pre-registration for Gold, Platinum and
// Champions, as the read's `registrationsOpenAt`), or it has an entry in any list. From then on its dates change only from its page.
export const registrationsOpen = (event, now = Date.now()) => !event.registrationsOpenAt || Date.parse(event.registrationsOpenAt) <= now
  || event.hasEntries
// What can be moved on the calendar: an open proposal (ticket 15), and a published event until its registrations open (Phase E3), as the
// read names its dates revision (an older API, which cannot move it, does not).
export const movable = item => (item.kind === "event" ? Boolean(item.datesRevision) && !registrationsOpen(item) : OPEN_STATES.includes(item.state))
export const queueItems = (items, queue, now) => {
  const picked = QUEUES.find(({ key }) => key === queue)
  return picked ? items.filter(item => picked.holds(item, now)) : items
}
